import React from 'react';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useRouteLoaderData,
  useSubmit
} from 'react-router-dom';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  CalendarIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  UserIcon,
  TicketIcon,
  CheckIcon,
  BuildingOfficeIcon,
  TagIcon,
  UserCircleIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { PageTitle } from '@oysterjs/ui/page';
import { Badge } from '@oysterjs/ui/badge';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@oysterjs/ui/table';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { Await } from '@oysterjs/ui/async';
import { PageNavigation } from '@oysterjs/ui/navigation';
import {
  Action,
  AddServiceRequestCommentInput,
  AgencyUserFieldsFragment,
  CallTranscriptFieldsFragment,
  CallTranscriptRole,
  CaraWorkspaceMessageInput,
  CreateCaraWorkspaceMutation,
  CreateCaraWorkspaceDocument,
  EmailSenderRole,
  EmailThreadConversationFieldsFragment,
  Resource,
  ResolveServiceRequestInput,
  ResolveServiceRequestMutation,
  ServiceRequestCallFieldsFragment,
  ServiceRequestFieldsFragment,
  ServiceRequestState,
  ServiceRequestType,
  ServiceRequestUpdateType,
  UpdateInitiatorType,
  UpdateServiceRequestInput,
  UpdateServiceRequestMutation,
  CaraWorkspacePreset,
  DocumentType,
  CaraWorkspaceExistingFileInput,
  CaraWorkspaceAmsContextInput,
  CaraWorkspaceAmsRefInput,
  useUpdateServiceRequestMutation,
  useGetCustomPresetsQuery
} from '~/api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';
import { Textarea } from '@oysterjs/ui/textarea';
import { FileInput, mergeFileInputChanges, type FileInputChange } from '~/components/file';
import { CREATE_CARA_WORKSPACE, PermissionNotice, usePermissions } from '~/components/permissions';
import { client } from '~/api/client';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { getDisplayInsuranceType } from '~/lib/insurance';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
import { Combobox } from '@oysterjs/ui/combobox';
import { AcordDocumentPicker } from '~/components/presets/acord-document-picker';
import { acordDocumentOptions } from '~/components/presets/acord-document-options';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const getAssignee = (serviceRequest: ServiceRequestFieldsFragment) => {
  if (serviceRequest.assignee) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="rounded-full size-7 bg-blue-300/50 dark:bg-blue-500/30 px-3 py-1 cursor-default text-xs font-medium inline-flex flex-row items-center justify-center gap-1"
          aria-hidden
        >
          {serviceRequest.assignee.name
            .split(' ')
            .map((name) => name[0])
            .join('')
            .toUpperCase()}
        </div>
        <span>{serviceRequest.assignee.name}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="text-sm text-neutral-500 dark:text-neutral-400">&mdash;</div>
    </div>
  );
};

const searchJson = (s: ServiceRequestFieldsFragment, search: string) => {
  const values = [s.account?.account.name || '', s.state, s.type, s.assignee?.name || ''];
  return values.some((v) => v?.toString()?.toLowerCase().indexOf(search.toLowerCase()) !== -1);
};

export const ServicingPage = () => {
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<ServiceRequestState | null>(ServiceRequestState.Open);
  const [assignee] = React.useState<string | null>(null);

  const loader = useLoaderData() as
    | { data: Promise<{ serviceRequests?: ServiceRequestFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Servicing" description="Service requests from your customers">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full">
              <Fieldset>
                <FieldGroup>
                  <div className="flex gap-4">
                    <Field className="w-full max-w-md">
                      <Label className="sr-only" data-slot="none">
                        Filter table
                      </Label>
                      <InputGroup>
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <Input
                          type="search"
                          name="search"
                          placeholder="Start typing to filter..."
                          onChange={(e) => setSearch(e.currentTarget.value)}
                        />
                      </InputGroup>
                    </Field>
                    <Field>
                      <Label className="sr-only" data-slot="none">
                        Filter by status
                      </Label>
                      <Select
                        name="status"
                        value={status || ''}
                        onChange={(e) => setStatus(e.currentTarget.value as ServiceRequestState)}
                      >
                        <option value="" disabled>
                          Filter by status
                        </option>
                        <option value={ServiceRequestState.Open}>Open</option>
                        <option value={ServiceRequestState.Closed}>Closed</option>
                        <option value="">All</option>
                      </Select>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </form>
          </div>
        </div>

        <React.Suspense fallback={<ServiceRequestsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ serviceRequests }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
                {!serviceRequests?.length && (
                  <div className="text-center py-12">
                    <TicketIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                      No service requests found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      Your clients haven't made any service requests yet.
                    </p>
                  </div>
                )}
                {!!serviceRequests?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Customer</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Request Type</TableHeader>
                        <TableHeader className="py-4">Assignee</TableHeader>
                        <TableHeader className="py-4">Due Date</TableHeader>
                        <TableHeader className="py-4">Opened At</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceRequests
                        .filter(
                          (s) =>
                            (!search || searchJson(s, search)) &&
                            (!status || s.state === status) &&
                            (!assignee || s.assignee?.id === assignee)
                        )
                        .map((serviceRequest) => (
                          <TableRow
                            key={serviceRequest.id}
                            href={`/app/servicing/${serviceRequest.id}/overview`}
                          >
                            <TableCell>
                              {serviceRequest.account?.account.name && (
                                <div className="font-medium flex gap-1 items-center">
                                  <div>{serviceRequest.account.account.name} </div>
                                  <div>
                                    <CheckBadgeIcon className="size-4 text-blue-400" />
                                  </div>
                                </div>
                              )}
                              {!serviceRequest.account?.account.name &&
                                !!serviceRequest.clientName && (
                                  <div className="font-medium">{serviceRequest.clientName}</div>
                                )}

                              {!serviceRequest.account?.account.name &&
                                !serviceRequest.clientName &&
                                !!serviceRequest.clientPhone && (
                                  <div className="font-medium">{serviceRequest.clientPhone}</div>
                                )}
                              {!serviceRequest.account?.account.name &&
                                !serviceRequest.clientName &&
                                !serviceRequest.clientPhone && (
                                  <div className="font-medium text-neutral-500 dark:text-neutral-400">
                                    —
                                  </div>
                                )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                // eslint-disable-next-line
                                // @ts-ignore
                                color={
                                  {
                                    [ServiceRequestState.Open]: 'amber',
                                    [ServiceRequestState.Closed]: 'green'
                                  }[serviceRequest.state] || 'neutral'
                                }
                              >
                                {serviceRequest.state}
                              </Badge>
                            </TableCell>
                            <TableCell>{titleCase(serviceRequest.type)}</TableCell>
                            <TableCell>{getAssignee(serviceRequest)}</TableCell>
                            <TableCell>
                              {serviceRequest.dueAt &&
                                new Intl.DateTimeFormat('en-US', {
                                  dateStyle: 'medium'
                                }).format(new Date(serviceRequest.dueAt))}
                              {!serviceRequest.dueAt && (
                                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                  &mdash;
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {serviceRequest.createdAt &&
                                new Intl.DateTimeFormat('en-US', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short'
                                }).format(new Date(serviceRequest.createdAt))}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
    </PageTitle>
  );
};

export const ServiceRequestsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading service requests...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

const UPDATE_SERVICE_REQUEST = [{ resource: Resource.ServiceRequest, action: Action.Update }];
const RESOLVE_SERVICE_REQUEST = [{ resource: Resource.ServiceRequest, action: Action.Resolve }];

export const ServiceRequestPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
          agencyUsers?: AgencyUserFieldsFragment[];
        }>;
      }
    | undefined;

  // Resolving, updating and asking Cara are three different permissions, so the
  // header offers whichever of them this caller actually holds.
  const { hasAllPermissions } = usePermissions();
  const canResolve = hasAllPermissions(RESOLVE_SERVICE_REQUEST);
  const canUpdate = hasAllPermissions(UPDATE_SERVICE_REQUEST);
  const canAskCara = hasAllPermissions(CREATE_CARA_WORKSPACE);

  const [showResolveServiceRequestDialog, setShowResolveServiceRequestDialog] =
    React.useState(false);
  const [showUpdateServiceRequestDialog, setShowUpdateServiceRequestDialog] = React.useState(false);
  const [showOpenInCopilotDialog, setShowOpenInCopilotDialog] = React.useState(false);

  return (
    <>
      <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
        <Await resolve={loader?.data}>
          {({ serviceRequest, agencyUsers }) =>
            serviceRequest &&
            agencyUsers && (
              <>
                <div className="lg:flex lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <h2 className="m-0 text-2xl font-semibold text-neutral-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight">
                      Service Request
                    </h2>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                        <BriefcaseIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                        />
                        {serviceRequest.account?.account.name || serviceRequest.clientName || '—'}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                        <TagIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                        />
                        {titleCase(serviceRequest.type)}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                        <CalendarIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                        />
                        Created on{' '}
                        {serviceRequest.createdAt &&
                          new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium'
                          }).format(new Date(serviceRequest.createdAt))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    {serviceRequest.state === ServiceRequestState.Open && canResolve && (
                      <Button
                        type="button"
                        color="sky"
                        onClick={() => setShowResolveServiceRequestDialog(true)}
                      >
                        <CheckIcon
                          aria-hidden="true"
                          className="-ml-0.5 mr-1.5 size-5 text-white-400"
                        />
                        Resolve
                      </Button>
                    )}
                    {serviceRequest.state === ServiceRequestState.Closed && (
                      <Button type="button" color="sky" disabled>
                        <CheckIcon
                          aria-hidden="true"
                          className="-ml-0.5 mr-1.5 size-5 text-white-400"
                        />
                        Resolved
                      </Button>
                    )}

                    {/* Dropdown */}
                    {(canAskCara || canUpdate) && (
                      <Menu as="div" className="relative ml-3">
                        <MenuButton className="inline-flex items-center rounded-md bg-white dark:bg-neutral-800 px-3 py-2 text-sm font-semibold text-neutral-900 dark:text-white shadow-sm ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600 hover:ring-neutral-400 dark:hover:ring-white/20">
                          More
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="-mr-1 ml-1.5 size-5 text-neutral-400 dark:text-neutral-500"
                          />
                        </MenuButton>

                        <MenuItems
                          transition
                          className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                        >
                          {canAskCara && (
                            <MenuItem>
                              <a
                                onClick={() => setShowOpenInCopilotDialog(true)}
                                className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10 data-[focus]:outline-none cursor-pointer"
                              >
                                Open in Copilot
                              </a>
                            </MenuItem>
                          )}
                          {canUpdate && (
                            <MenuItem>
                              <a
                                className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10 data-[focus]:outline-none cursor-pointer"
                                onClick={() => setShowUpdateServiceRequestDialog(true)}
                              >
                                Update request
                              </a>
                            </MenuItem>
                          )}
                        </MenuItems>
                      </Menu>
                    )}
                  </div>
                </div>
                <PageNavigation
                  routes={[
                    {
                      path: 'overview',
                      title: 'Overview'
                    },
                    {
                      path: 'conversation',
                      title: 'Conversation',
                      hidden: !serviceRequest.calls?.length && !serviceRequest.emailThreads?.length
                    },
                    {
                      path: 'activity',
                      title: 'Comments and Activity'
                    }
                  ]}
                />
                <UpdateServiceRequestDialog
                  open={showUpdateServiceRequestDialog}
                  onClose={() => setShowUpdateServiceRequestDialog(false)}
                  serviceRequest={serviceRequest}
                  agencyUsers={agencyUsers}
                />
                <OpenInCopilotDialog
                  open={showOpenInCopilotDialog}
                  onClose={() => setShowOpenInCopilotDialog(false)}
                  serviceRequest={serviceRequest}
                />
              </>
            )
          }
        </Await>
      </React.Suspense>
      <ResolveServiceRequestDialogWrapper
        open={showResolveServiceRequestDialog}
        onClose={() => setShowResolveServiceRequestDialog(false)}
      />
    </>
  );
};

const ResolveServiceRequestDialogWrapper = (props: { open: boolean; onClose: () => void }) => {
  const action = useActionData() as ActionResult<ResolveServiceRequestMutation>;
  const submit = useSubmit();
  const navigation = useNavigation();
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location.pathname) {
      props.onClose();
    }
  }, [navigation.state, navigation.formAction]);

  const handleSubmit = async (form: ResolveServiceRequestInput) => {
    if (!form) {
      return;
    }

    // Construct a multi-part form data request with the file
    const formData = new FormData();
    form.files.forEach((f) => formData.append('files', f));
    formData.append('notes', form.notes);

    submit(formData, { method: 'POST', action: 'resolve', encType: 'multipart/form-data' });
  };

  return (
    <ResolveServiceRequestDialog
      open={props.open}
      onClose={props.onClose}
      loading={navigation.state === 'submitting'}
      validationErrors={validationErrors}
      onSubmit={handleSubmit}
    />
  );
};

export const ResolveServiceRequestDialog = (props: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (form: ResolveServiceRequestInput) => Promise<void>;
  validationErrors: ValidationError[];
}) => {
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<ResolveServiceRequestInput>({
    files: [],
    notes: ''
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (props.validationErrors) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Resolve Service Request</DialogTitle>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit(form);
        }}
      >
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Notes</Label>
                <Description>These notes will not be visible to the client.</Description>
                <Textarea
                  value={form.notes || ''}
                  rows={3}
                  onChange={(e) => {
                    const notes = e.currentTarget.value;
                    updateForm(['notes'], (prev) => ({ ...prev, notes }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['notes'])}
                />
                {getValidationError(validationErrors, ['notes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['notes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Files</Label>
                <Description>
                  Drag and drop or click to select files from your computer. You can upload multiple
                  files at once. These files will be visible to the client as part of the service
                  request resolution.
                </Description>
                <FileInput
                  multiple
                  onChange={(change) =>
                    updateForm(['files'], (prev) => ({
                      ...prev,
                      files: change.files
                        .filter((f) => 'upload' in f && f.upload)
                        .map((f) => f.upload!)
                    }))
                  }
                  invalid={!!getValidationError(validationErrors, ['files'])}
                />
                {getValidationError(validationErrors, ['files']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['files'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={props.loading} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={props.loading}>
            Resolve Request
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

// Helper to check if a preset value is a built-in preset
const BUILT_IN_PRESET_VALUES = Object.values(CaraWorkspacePreset) as string[];
const isBuiltInPresetValue = (
  value: CaraWorkspacePreset | string | null | undefined
): value is CaraWorkspacePreset => {
  return value !== null && value !== undefined && BUILT_IN_PRESET_VALUES.includes(value);
};

const OpenInCopilotDialog = (props: {
  open: boolean;
  onClose: () => void;
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string>();
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { data: customPresetsData } = useGetCustomPresetsQuery();
  const customPresets = customPresetsData?.customPresets || [];
  const [preset, setPreset] = React.useState<CaraWorkspacePreset | string | null | undefined>(
    props.serviceRequest.type === ServiceRequestType.Question
      ? CaraWorkspacePreset.DocumentExploration
      : props.serviceRequest.type === ServiceRequestType.QuoteRequest
        ? CaraWorkspacePreset.GenerateTemplate
        : props.serviceRequest.type === ServiceRequestType.CreateCertificate
          ? CaraWorkspacePreset.GenerateTemplate
          : undefined
  );
  const [form, setForm] = React.useState<CaraWorkspaceMessageInput>({
    files: [],
    existingFiles: [],
    message: '',
    serviceRequestId: props.serviceRequest.id,
    documentIds:
      props.serviceRequest.type === ServiceRequestType.CreateCertificate
        ? [DocumentType.Acord_25]
        : []
  });
  const pinsByFieldRef = React.useRef<Map<string, FileInputChange>>(new Map());
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  // Lanes keep their AMS pins in local FileInput state and unmount on preset
  // change, so the per-lane snapshot has to be dropped here or a pin the user
  // can no longer see would still be submitted.
  const clearAmsContext = React.useCallback(() => {
    pinsByFieldRef.current.clear();
    setForm((prev) => ({ ...prev, amsContext: [], amsRefs: [] }));
  }, [setForm]);

  React.useEffect(() => {
    if (props.open) {
      setSubmitError(undefined);
      clearAmsContext();
    }
  }, [props.open, clearAmsContext]);

  const updateFiles = (change: FileInputChange, label = 'default') => {
    pinsByFieldRef.current.set(label, change);
    const { amsContext, amsRefs = [] } = mergeFileInputChanges(
      ...[...pinsByFieldRef.current.values()].map((pins) => ({
        files: [],
        amsContext: pins.amsContext,
        amsRefs: pins.amsRefs
      }))
    );

    updateForm(['files', 'existingFiles', 'amsContext', 'amsRefs'], (prev) => {
      const newUploads: File[] = [];
      const newExisting: CaraWorkspaceExistingFileInput[] = [];

      for (const fileInput of change.files) {
        if ('upload' in fileInput && fileInput.upload) {
          const originalFile = fileInput.upload as File;
          const shouldRename = typeof File !== 'undefined' && !!label;
          const renamed = shouldRename
            ? new File([originalFile], `${label} ${originalFile.name}`, {
                type: originalFile.type,
                lastModified: originalFile.lastModified
              })
            : originalFile;
          newUploads.push(renamed);
        } else if ('existing' in fileInput && fileInput.existing) {
          newExisting.push({
            id: fileInput.existing.id,
            integrationId: fileInput.existing.integrationId ?? undefined
          });
        }
      }

      // Append and de-duplicate uploads by name+size+lastModified
      const combinedUploads = [...(prev.files || []), ...newUploads];
      const uploadSeen = new Set<string>();
      const dedupedUploads = combinedUploads.filter((f) => {
        const key = `${f.name}:${f.size}:${(f as File).lastModified}`;
        if (uploadSeen.has(key)) return false;
        uploadSeen.add(key);
        return true;
      });

      // Append and de-duplicate existing by id+integrationId
      const combinedExisting = [...(prev.existingFiles || []), ...newExisting];
      const existingSeen = new Set<string>();
      const dedupedExisting = combinedExisting.filter((e) => {
        const key = `${e.id}:${e.integrationId || ''}`;
        if (existingSeen.has(key)) return false;
        existingSeen.add(key);
        return true;
      });

      return {
        ...prev,
        files: dedupedUploads,
        existingFiles: dedupedExisting,
        amsContext: amsContext.length > 0 ? amsContext : undefined,
        amsRefs: amsRefs.length > 0 ? amsRefs : undefined
      };
    });
  };

  const updateMessage = (message: string) => {
    updateForm(['message'], (prev) => ({ ...prev, message }));
  };

  const updateDocumentIds = (documentIds: DocumentType[]) => {
    updateForm(['documentIds'], (prev) => ({
      ...prev,
      documentIds
    }));
  };

  const createWorkspace = async (
    query: string,
    uploads: File[],
    existing: CaraWorkspaceExistingFileInput[],
    serviceRequestId: string,
    documentIds: DocumentType[],
    amsContext?: CaraWorkspaceAmsContextInput[],
    amsRefs?: CaraWorkspaceAmsRefInput[]
  ) => {
    // Determine if preset is a built-in preset or custom preset ID
    const builtInPreset = isBuiltInPresetValue(preset) ? preset : undefined;
    const customPresetId =
      !isBuiltInPresetValue(preset) && preset !== null && preset !== undefined
        ? (preset as string)
        : undefined;

    const res = await client.mutate<CreateCaraWorkspaceMutation>({
      mutation: CreateCaraWorkspaceDocument,
      variables: {
        input: {
          preset: builtInPreset,
          customPresetId: customPresetId,
          message: query.trim(),
          files: uploads,
          existingFiles: existing,
          serviceRequestId,
          documentIds,
          amsContext: amsContext?.length ? amsContext : undefined,
          amsRefs: amsRefs?.length ? amsRefs : undefined
        }
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    const id = res.data?.createCaraWorkspace?.id;
    if (!id) {
      throw new Error('Failed to create workspace');
    }

    navigate(`/app/workspaces/${id}`);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    setLoading(true);
    setSubmitError(undefined);

    try {
      await createWorkspace(
        form.message,
        form.files,
        form.existingFiles || [],
        props.serviceRequest.id,
        form.documentIds || [],
        form.amsContext ?? undefined,
        form.amsRefs ?? undefined
      );
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationError = getApplicationError(err);
        const validationErrs = getValidationErrors(err);
        setSubmitError(
          applicationError?.message ??
            validationErrs?.[0]?.message ??
            err.message ??
            'Failed to create workspace'
        );
      } else {
        setSubmitError('Failed to create workspace');
      }
    } finally {
      setLoading(false);
    }
  };

  const presetOptions = [
    {
      preset: CaraWorkspacePreset.CertificateReview,
      title: 'Certificate Review',
      description: 'Review a certificate for any discrepancies and E&O exposure'
    },
    {
      preset: CaraWorkspacePreset.ContractReview,
      title: 'Contract Review',
      description:
        'Review a contract for insurance requirements and compare it to quote or policy details'
    },
    {
      preset: CaraWorkspacePreset.CoverageComparison,
      title: 'Coverage Comparison',
      description: 'Compare coverage details between multiple quotes or policies'
    },
    {
      preset: CaraWorkspacePreset.DocumentExploration,
      title: 'Explore a Policy',
      description: 'Ask Cara to answer questions about a policy document',
      suggested: props.serviceRequest.type === ServiceRequestType.Question
    },
    {
      preset: CaraWorkspacePreset.GenerateTemplate,
      title: 'Fill Template',
      description: 'Cross-fill forms and templates from other documents and instructions',
      suggested:
        props.serviceRequest.type === ServiceRequestType.QuoteRequest ||
        props.serviceRequest.type === ServiceRequestType.CreateCertificate
    },
    {
      preset: CaraWorkspacePreset.CommercialClassification,
      title: 'Commercial Classification',
      description: 'Classify business operations using NAICS, SIC, or GL codes'
    },
    // Add custom presets
    ...customPresets.map((cp) => ({
      preset: cp.id,
      title: cp.title,
      description: cp.description || '',
      suggested: false
    })),
    {
      preset: null,
      title: 'Custom',
      description:
        'Do something else. Cara will perform better if your task fits one of the above options.',
      suggested: false
    }
  ].sort((a, b) => (a.suggested ? (b.suggested ? (a.title < b.title ? -1 : 1) : -1) : 1));

  // Check if preset is a custom preset or the Custom option (derived from preset state)
  const isCustomPresetOrOption = React.useMemo(
    () => preset === null || (preset !== undefined && !isBuiltInPresetValue(preset)),
    [preset]
  );

  return (
    <Dialog open={props.open} onClose={props.onClose} size="xl">
      <DialogTitle>Open in Copilot</DialogTitle>
      <DialogDescription>
        Ask Cara to help you resolve this service request. Specify what task you want Cara to do and
        include any additional instructios.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>How should Cara help?</Label>
                <Description>Choose what you want Cara to do</Description>
                <Combobox
                  value={preset === null ? 'Custom' : preset}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(value) => {
                    clearAmsContext();
                    if (value === 'Custom') {
                      setPreset(null);
                    } else {
                      // Value can be either CaraWorkspacePreset enum or custom preset ID string
                      setPreset(value as CaraWorkspacePreset | string);
                    }
                  }}
                  options={presetOptions.map((p) => ({
                    value: p.preset || 'Custom',
                    label: p.title,
                    description: p.description,
                    badgeText: p.suggested ? 'Suggested' : undefined
                  }))}
                  error={getValidationError(validationErrors, ['preset'])}
                />
              </Field>
            </FieldGroup>
            {preset === CaraWorkspacePreset.CertificateReview && (
              <FieldGroup>
                <Field>
                  <Label>Certificate of Insurance</Label>
                  <Description>
                    Upload the certificate of insurance you want Cara to review.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Certificate')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Quotes or Policies</Label>
                  <Description>
                    Upload any quotes or policies you want Cara to compare to the certificate of
                    insurance.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Quote or Policy')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when reviewing the
                    certificate of insurance.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.ContractReview && (
              <FieldGroup>
                <Field>
                  <Label>Contract</Label>
                  <Description>
                    Upload the contract you want Cara to review for insurance requirements.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Contract')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Quotes or Policies</Label>
                  <Description>
                    Upload any quotes or policies you want Cara to compare to the contract
                    requirements.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Quote or Policy')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when reviewing the contract.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.CoverageComparison && (
              <FieldGroup>
                <Field>
                  <Label>First Quote or Policy</Label>
                  <Description>
                    Upload the first quote or policy you want Cara to compare to the second quote or
                    policy.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Option 1')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Second Quote or Policy</Label>
                  <Description>
                    Upload the second quote or policy you want Cara to compare to the first quote or
                    policy.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Option 2')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when comparing the quotes or
                    policies.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.GenerateTemplate && (
              <FieldGroup>
                <Field>
                  <Label>Forms and Certificates</Label>
                  <Description>
                    Choose the forms and certificates you'd like Cara to fill out.
                  </Description>
                  <AcordDocumentPicker
                    options={acordDocumentOptions}
                    value={form.documentIds || []}
                    onChange={updateDocumentIds}
                    placeholder="Search templates"
                  />
                </Field>
                <Field>
                  <Label>Input Files</Label>
                  <Description>
                    Any files you'd like Cara to parse and use as input to cross-file into
                    templates.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Input')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Additional Information</Label>
                  <Description>
                    Add any additional information you want Cara to consider when filling out the
                    template. This can be specific instructions or other free-form information that
                    should be parsed and mapped to template fields
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    rows={5}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.DocumentExploration && (
              <FieldGroup>
                <Field>
                  <Label>Input Files</Label>
                  <Description>
                    Files you'd like Cara to use as context to answer questions
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change, 'Input')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Question or Prompt</Label>
                  <Description>
                    Any question or prompt you'd like Cara to answer about the documents.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    rows={5}
                  />
                </Field>
              </FieldGroup>
            )}

            {isCustomPresetOrOption && (
              <FieldGroup>
                <Field>
                  <Label>Instructions</Label>
                  <Description>
                    Any additional instructions to Cara to help resolve the service request, aside
                    from the information already captured.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                  {getValidationError(validationErrors, ['message']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['message'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Files</Label>
                  <Description>
                    Drag and drop or click to select files from your computer. You can upload
                    multiple files at once. These files will be visible to the client as part of the
                    service request resolution.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => updateFiles(change)}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                  {getValidationError(validationErrors, ['files']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['files'])}</ErrorMessage>
                  )}
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
              </FieldGroup>
            )}
          </Fieldset>
          {submitError && (
            <p className="text-sm text-red-600 dark:text-red-500" role="alert">
              {submitError}
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={loading} disabled={preset === undefined}>
            Open in Copilot
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const AlreadyIncludedFiles = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
}) => {
  const files = props.serviceRequest.emailThreads?.flatMap((thread) => thread.attachments);
  if (!files?.length) {
    return null;
  }

  return (
    <Field>
      <Label>Already Included Files</Label>
      <Description>
        These files will automatically be included in the Copilot workspace and do not need to be
        uploaded again.
      </Description>
      <div data-slot="control" className="flex flex-wrap flex-row gap-2">
        {files.map((file) => (
          <Link
            key={file.id}
            to={file.url || ''}
            target="_blank"
            className="rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1 hover:shadow hover:cursor-pointer transition-all duration-150"
          >
            <PaperClipIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{file.name}</p>
          </Link>
        ))}
      </div>
    </Field>
  );
};

const UpdateServiceRequestDialog = (props: {
  open: boolean;
  onClose: () => void;
  serviceRequest: ServiceRequestFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const action = useActionData() as ActionResult<UpdateServiceRequestMutation>;
  const submit = useSubmit();
  const navigation = useNavigation();

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<UpdateServiceRequestInput>({
    assigneeId: null,
    dueAt: null
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location.pathname) {
      props.onClose();
    }
  }, [navigation.state, navigation.formAction]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    submit(
      {
        assigneeId: form.assigneeId || null,
        dueAt: form.dueAt?.toISOString() || null
      },
      { method: 'POST', action: 'update', encType: 'application/json' }
    );
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Update Service Request</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Assignee</Label>
                <Description>
                  The team member that will be assigned to this service request.
                </Description>
                <Select
                  value={form.assigneeId || ''}
                  onChange={(e) => {
                    const assigneeId = e.currentTarget.value;
                    updateForm(['assigneeId'], (prev) => ({ ...prev, assigneeId }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['assigneeId'])}
                >
                  <option value="">Unassigned</option>
                  {props.agencyUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
                {getValidationError(validationErrors, ['notes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['notes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Due Date</Label>
                <Description>The date by which the service request must be resolved.</Description>
                <Input
                  type="date"
                  value={form.dueAt ? new Date(form.dueAt).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const dueAt = e.currentTarget.value;
                    updateForm(['dueAt'], (prev) => ({
                      ...prev,
                      dueAt: new Date(dueAt)
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['dueAt'])}
                />
                {getValidationError(validationErrors, ['dueAt']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['dueAt'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={navigation.state === 'submitting'}>
            Update Request
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const buildFormFromServiceRequest = (
  serviceRequest: ServiceRequestFieldsFragment
): UpdateServiceRequestInput => ({
  clientName: serviceRequest.clientName,
  clientEmail: serviceRequest.clientEmail,
  clientPhone: serviceRequest.clientPhone,
  details: serviceRequest.details,
  addAdditionalInsured: serviceRequest.addAdditionalInsured
    ? {
        name: serviceRequest.addAdditionalInsured.name,
        address: {
          line1: serviceRequest.addAdditionalInsured.address.line1,
          city: serviceRequest.addAdditionalInsured.address.city,
          zone: serviceRequest.addAdditionalInsured.address.zone,
          postalCode: serviceRequest.addAdditionalInsured.address.postalCode
        },
        relationship: serviceRequest.addAdditionalInsured.relationship
      }
    : undefined,
  cancelPolicy: serviceRequest.cancelPolicy
    ? {
        policyId: serviceRequest.cancelPolicy.policyId,
        reason: serviceRequest.cancelPolicy.reason
      }
    : undefined,
  createCertificate: serviceRequest.createCertificate
    ? {
        name: serviceRequest.createCertificate.name,
        address: {
          line1: serviceRequest.createCertificate.address.line1,
          city: serviceRequest.createCertificate.address.city,
          zone: serviceRequest.createCertificate.address.zone,
          postalCode: serviceRequest.createCertificate.address.postalCode
        },
        additionalInsured: serviceRequest.createCertificate.additionalInsured,
        additionalInsuredRelationship:
          serviceRequest.createCertificate.additionalInsuredRelationship,
        additionalInsuredRelationshipDescription:
          serviceRequest.createCertificate.additionalInsuredRelationshipDescription
      }
    : undefined,
  question: serviceRequest.question
    ? {
        policyId: serviceRequest.question.policyId,
        question: serviceRequest.question.question,
        type: serviceRequest.question.type
      }
    : undefined
});

const EditServiceRequestDetailsDialog = (props: {
  open: boolean;
  onClose: () => void;
  serviceRequest: ServiceRequestFieldsFragment;
  onUpdate: (updated: ServiceRequestFieldsFragment) => void;
}) => {
  const [updateServiceRequest, { loading }] = useUpdateServiceRequestMutation();

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<UpdateServiceRequestInput>(() =>
    buildFormFromServiceRequest(props.serviceRequest)
  );
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  // Reset form when dialog opens with new serviceRequest
  React.useEffect(() => {
    if (props.open) {
      setForm(buildFormFromServiceRequest(props.serviceRequest));
      setValidationErrors([]);
    }
  }, [props.open, props.serviceRequest]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form) return;

    const result = await updateServiceRequest({
      variables: {
        id: props.serviceRequest.id,
        input: {
          clientName: form.clientName || null,
          clientEmail: form.clientEmail || null,
          clientPhone: form.clientPhone || null,
          details: form.details || null,
          addAdditionalInsured: form.addAdditionalInsured || null,
          cancelPolicy: form.cancelPolicy || null,
          createCertificate: form.createCertificate || null,
          question: form.question || null
        }
      }
    });

    if (result.data?.updateServiceRequest) {
      props.onUpdate(result.data.updateServiceRequest);
      props.onClose();
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} size="2xl">
      <DialogTitle>Edit Service Request Details</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 max-h-[60vh] overflow-y-auto">
          <Fieldset>
            <FieldGroup>
              {/* Common client details */}
              <Field>
                <Label>Client Name</Label>
                <Input
                  value={form.clientName || ''}
                  onChange={(e) => {
                    const clientName = e.currentTarget.value;
                    updateForm(['clientName'], (prev) => ({ ...prev, clientName }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['clientName'])}
                />
                {getValidationError(validationErrors, ['clientName']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['clientName'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={form.clientEmail || ''}
                  onChange={(e) => {
                    const clientEmail = e.currentTarget.value;
                    updateForm(['clientEmail'], (prev) => ({ ...prev, clientEmail }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['clientEmail'])}
                />
                {getValidationError(validationErrors, ['clientEmail']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['clientEmail'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Client Phone</Label>
                <Input
                  type="tel"
                  value={form.clientPhone || ''}
                  onChange={(e) => {
                    const clientPhone = e.currentTarget.value;
                    updateForm(['clientPhone'], (prev) => ({ ...prev, clientPhone }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['clientPhone'])}
                />
                {getValidationError(validationErrors, ['clientPhone']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['clientPhone'])}
                  </ErrorMessage>
                )}
              </Field>

              {/* Type-specific fields */}
              {props.serviceRequest.type === ServiceRequestType.AddAdditionalInsured && (
                <>
                  <Field>
                    <Label>Additional Insured Name</Label>
                    <Input
                      value={form.addAdditionalInsured?.name || ''}
                      onChange={(e) => {
                        const name = e.currentTarget.value;
                        updateForm(['addAdditionalInsured', 'name'], (prev) => ({
                          ...prev,
                          addAdditionalInsured: { ...prev.addAdditionalInsured!, name }
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['addAdditionalInsured', 'name'])
                      }
                    />
                  </Field>
                  <Field>
                    <Label>Address Line 1</Label>
                    <Input
                      value={form.addAdditionalInsured?.address?.line1 || ''}
                      onChange={(e) => {
                        const line1 = e.currentTarget.value;
                        updateForm(['addAdditionalInsured', 'address', 'line1'], (prev) => ({
                          ...prev,
                          addAdditionalInsured: {
                            ...prev.addAdditionalInsured!,
                            address: { ...prev.addAdditionalInsured!.address, line1 }
                          }
                        }));
                      }}
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-4">
                    <Field>
                      <Label>City</Label>
                      <Input
                        value={form.addAdditionalInsured?.address?.city || ''}
                        onChange={(e) => {
                          const city = e.currentTarget.value;
                          updateForm(['addAdditionalInsured', 'address', 'city'], (prev) => ({
                            ...prev,
                            addAdditionalInsured: {
                              ...prev.addAdditionalInsured!,
                              address: { ...prev.addAdditionalInsured!.address, city }
                            }
                          }));
                        }}
                      />
                    </Field>
                    <Field>
                      <Label>State</Label>
                      <Input
                        value={form.addAdditionalInsured?.address?.zone || ''}
                        onChange={(e) => {
                          const zone = e.currentTarget.value;
                          updateForm(['addAdditionalInsured', 'address', 'zone'], (prev) => ({
                            ...prev,
                            addAdditionalInsured: {
                              ...prev.addAdditionalInsured!,
                              address: { ...prev.addAdditionalInsured!.address, zone }
                            }
                          }));
                        }}
                      />
                    </Field>
                    <Field>
                      <Label>Postal Code</Label>
                      <Input
                        value={form.addAdditionalInsured?.address?.postalCode || ''}
                        onChange={(e) => {
                          const postalCode = e.currentTarget.value;
                          updateForm(['addAdditionalInsured', 'address', 'postalCode'], (prev) => ({
                            ...prev,
                            addAdditionalInsured: {
                              ...prev.addAdditionalInsured!,
                              address: { ...prev.addAdditionalInsured!.address, postalCode }
                            }
                          }));
                        }}
                      />
                    </Field>
                  </div>
                  <Field>
                    <Label>Relationship</Label>
                    <Input
                      value={form.addAdditionalInsured?.relationship || ''}
                      onChange={(e) => {
                        const relationship = e.currentTarget.value;
                        updateForm(['addAdditionalInsured', 'relationship'], (prev) => ({
                          ...prev,
                          addAdditionalInsured: { ...prev.addAdditionalInsured!, relationship }
                        }));
                      }}
                    />
                  </Field>
                </>
              )}

              {props.serviceRequest.type === ServiceRequestType.CancelPolicy && (
                <Field>
                  <Label>Cancellation Reason</Label>
                  <Textarea
                    value={form.cancelPolicy?.reason || ''}
                    onChange={(e) => {
                      const reason = e.currentTarget.value;
                      updateForm(['cancelPolicy', 'reason'], (prev) => ({
                        ...prev,
                        cancelPolicy: { ...prev.cancelPolicy!, reason }
                      }));
                    }}
                    rows={3}
                  />
                </Field>
              )}

              {props.serviceRequest.type === ServiceRequestType.CreateCertificate && (
                <>
                  <Field>
                    <Label>Certificate Holder Name</Label>
                    <Input
                      value={form.createCertificate?.name || ''}
                      onChange={(e) => {
                        const name = e.currentTarget.value;
                        updateForm(['createCertificate', 'name'], (prev) => ({
                          ...prev,
                          createCertificate: { ...prev.createCertificate!, name }
                        }));
                      }}
                    />
                  </Field>
                  <Field>
                    <Label>Address Line 1</Label>
                    <Input
                      value={form.createCertificate?.address?.line1 || ''}
                      onChange={(e) => {
                        const line1 = e.currentTarget.value;
                        updateForm(['createCertificate', 'address', 'line1'], (prev) => ({
                          ...prev,
                          createCertificate: {
                            ...prev.createCertificate!,
                            address: { ...prev.createCertificate!.address, line1 }
                          }
                        }));
                      }}
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-4">
                    <Field>
                      <Label>City</Label>
                      <Input
                        value={form.createCertificate?.address?.city || ''}
                        onChange={(e) => {
                          const city = e.currentTarget.value;
                          updateForm(['createCertificate', 'address', 'city'], (prev) => ({
                            ...prev,
                            createCertificate: {
                              ...prev.createCertificate!,
                              address: { ...prev.createCertificate!.address, city }
                            }
                          }));
                        }}
                      />
                    </Field>
                    <Field>
                      <Label>State</Label>
                      <Input
                        value={form.createCertificate?.address?.zone || ''}
                        onChange={(e) => {
                          const zone = e.currentTarget.value;
                          updateForm(['createCertificate', 'address', 'zone'], (prev) => ({
                            ...prev,
                            createCertificate: {
                              ...prev.createCertificate!,
                              address: { ...prev.createCertificate!.address, zone }
                            }
                          }));
                        }}
                      />
                    </Field>
                    <Field>
                      <Label>Postal Code</Label>
                      <Input
                        value={form.createCertificate?.address?.postalCode || ''}
                        onChange={(e) => {
                          const postalCode = e.currentTarget.value;
                          updateForm(['createCertificate', 'address', 'postalCode'], (prev) => ({
                            ...prev,
                            createCertificate: {
                              ...prev.createCertificate!,
                              address: { ...prev.createCertificate!.address, postalCode }
                            }
                          }));
                        }}
                      />
                    </Field>
                  </div>
                  <Field>
                    <Label>Additional Insured Relationship</Label>
                    <Input
                      value={form.createCertificate?.additionalInsuredRelationship || ''}
                      onChange={(e) => {
                        const additionalInsuredRelationship = e.currentTarget.value;
                        updateForm(
                          ['createCertificate', 'additionalInsuredRelationship'],
                          (prev) => ({
                            ...prev,
                            createCertificate: {
                              ...prev.createCertificate!,
                              additionalInsuredRelationship
                            }
                          })
                        );
                      }}
                    />
                  </Field>
                </>
              )}

              {props.serviceRequest.type === ServiceRequestType.Question && (
                <Field>
                  <Label>Question</Label>
                  <Textarea
                    value={form.question?.question || ''}
                    onChange={(e) => {
                      const question = e.currentTarget.value;
                      updateForm(['question', 'question'], (prev) => ({
                        ...prev,
                        question: { ...prev.question!, question }
                      }));
                    }}
                    rows={3}
                  />
                </Field>
              )}

              {/* General details field - for types that use it */}
              {(props.serviceRequest.type === ServiceRequestType.Other ||
                props.serviceRequest.type === ServiceRequestType.QuoteRequest ||
                props.serviceRequest.type === ServiceRequestType.EndorsePolicy ||
                props.serviceRequest.type === ServiceRequestType.DocumentRequest ||
                props.serviceRequest.details) && (
                <Field>
                  <Label>Additional Details</Label>
                  <Textarea
                    value={form.details || ''}
                    onChange={(e) => {
                      const details = e.currentTarget.value;
                      updateForm(['details'], (prev) => ({ ...prev, details }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['details'])}
                    rows={4}
                  />
                  {getValidationError(validationErrors, ['details']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['details'])}</ErrorMessage>
                  )}
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const ServiceRequestOverviewPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest }) =>
          serviceRequest && (
            <div className="flex gap-8">
              <ServiceRequestDetailsPanel serviceRequest={serviceRequest} />
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const ServiceRequestDetailsPanel = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  const [showEditDetailsDialog, setShowEditDetailsDialog] = React.useState(false);
  const [currentServiceRequest, setCurrentServiceRequest] = React.useState(props.serviceRequest);

  // Sync with props when the loader data changes
  React.useEffect(() => {
    setCurrentServiceRequest(props.serviceRequest);
  }, [props.serviceRequest]);

  const handleUpdate = (updated: ServiceRequestFieldsFragment) => {
    setCurrentServiceRequest(updated);
  };

  return (
    <>
      <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow flex-grow flex-shrink-0">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
            <div>
              <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                {titleCase(currentServiceRequest.type)}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Details about this service request.
              </p>
            </div>
            <Button type="button" color="white" onClick={() => setShowEditDetailsDialog(true)}>
              Edit Details
            </Button>
          </div>
        </div>
        <div className="py-2">
          {currentServiceRequest.type === ServiceRequestType.AddAdditionalInsured && (
            <ServiceRequestAddAdditionalInsuredDetails serviceRequest={currentServiceRequest} />
          )}
          {currentServiceRequest.type === ServiceRequestType.CancelPolicy && (
            <ServiceRequestCancelPolicyDetails serviceRequest={currentServiceRequest} />
          )}
          {currentServiceRequest.type === ServiceRequestType.CreateCertificate && (
            <ServiceRequestCreateCertificateDetails serviceRequest={currentServiceRequest} />
          )}
          {currentServiceRequest.type === ServiceRequestType.DocumentRequest && (
            <ServiceRequestDocumentRequestDetails serviceRequest={currentServiceRequest} />
          )}
          {currentServiceRequest.type === ServiceRequestType.EndorsePolicy && (
            <ServiceRequestEndorsePolicyDetails serviceRequest={currentServiceRequest} />
          )}
          {(currentServiceRequest.type === ServiceRequestType.Other ||
            currentServiceRequest.type === ServiceRequestType.QuoteRequest) && (
            <ServiceRequestOtherDetails serviceRequest={currentServiceRequest} />
          )}
          {currentServiceRequest.type === ServiceRequestType.Question && (
            <ServiceRequestQuestionDetails serviceRequest={currentServiceRequest} />
          )}
        </div>
      </div>
      <EditServiceRequestDetailsDialog
        open={showEditDetailsDialog}
        onClose={() => setShowEditDetailsDialog(false)}
        serviceRequest={currentServiceRequest}
        onUpdate={handleUpdate}
      />
    </>
  );
};

const ServiceRequestCommonDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <>
      {props.serviceRequest.clientName && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Client Name
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientName}
          </dd>
        </div>
      )}
      {props.serviceRequest.clientEmail && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Client Email
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientEmail}
          </dd>
        </div>
      )}
      {props.serviceRequest.clientPhone && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Client Phone
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientPhone}
          </dd>
        </div>
      )}
    </>
  );
};

const ServiceRequestAddAdditionalInsuredDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container flex-grow flex-shrink-0">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Additional Insured
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.addAdditionalInsured?.name}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Address</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.addAdditionalInsured?.address?.line1}
            <br />
            {props.serviceRequest.addAdditionalInsured?.address?.city},{' '}
            {props.serviceRequest.addAdditionalInsured?.address?.zone}{' '}
            {props.serviceRequest.addAdditionalInsured?.address?.postalCode}
          </dd>
        </div>
        {props.serviceRequest.addAdditionalInsured?.relationship && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Relationship
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.addAdditionalInsured?.relationship}
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Additional Notes
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestCancelPolicyDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Cancellation Details
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.cancelPolicy?.reason}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const ServiceRequestCreateCertificateDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Certificate Holder
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.name}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Address</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.address?.line1}
            <br />
            {props.serviceRequest.createCertificate?.address?.city},{' '}
            {props.serviceRequest.createCertificate?.address?.zone}{' '}
            {props.serviceRequest.createCertificate?.address?.postalCode}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Additional Insured
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.additionalInsured ? 'Yes' : 'No'}
          </dd>
        </div>
        {props.serviceRequest.createCertificate?.additionalInsured && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Relationship
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.createCertificate?.additionalInsuredRelationship}
              {props.serviceRequest.createCertificate?.additionalInsuredRelationshipDescription}
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Additional Notes
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestDocumentRequestDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Document Type
          </dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {titleCase(props.serviceRequest.documentRequest?.type || '')}
          </dd>
        </div>
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Request Details
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestEndorsePolicyDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Endorsement Details
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestOtherDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Request Details
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              <pre className="whitespace-break-spaces font-body">
                {props.serviceRequest.details}
              </pre>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestQuestionDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Question</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
            {props.serviceRequest.question?.question}
          </dd>
        </div>
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              Endorsement Details
            </dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

export const ServiceRequestConversationPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest }) =>
          serviceRequest && (
            <>
              <div className="flex flex-col space-y-4 p-4">
                {parseConversation(serviceRequest.calls, serviceRequest.emailThreads).map(
                  (message) =>
                    [
                      EmailSenderRole.Client,
                      EmailSenderRole.Participant,
                      EmailSenderRole.Agent
                    ].includes(message.role) ? (
                      <div className="flex items-start space-x-4 max-w-xl">
                        <div className="flex-shrink-0">
                          <div className="size-10 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                            <UserIcon className="size-6 text-neutral-500 dark:text-neutral-400" />
                          </div>
                        </div>
                        <div className="flex-1 bg-neutral-100 dark:bg-neutral-700 rounded-lg p-4">
                          <p className="text-sm text-neutral-900 dark:text-white">
                            {message.content}
                          </p>
                          {/* <span className="text-xs text-neutral-500 mt-1 block">10:23 AM</span> */}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start space-x-4 max-w-xl self-end">
                        <div className="flex-1 bg-sky-100 dark:bg-sky-500/20 rounded-lg p-4">
                          <p className="text-sm text-neutral-900 dark:text-white">
                            {message.content}
                          </p>
                          {/* <span className="text-xs text-neutral-500 mt-1 block">10:24 AM</span> */}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="size-10 rounded-full bg-sky-200 dark:bg-sky-500/25 flex items-center justify-center">
                            <BuildingOfficeIcon className="size-6 text-sky-600 dark:text-sky-400" />
                          </div>
                        </div>
                      </div>
                    )
                )}
              </div>
            </>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const parseConversation = (
  calls: CallTranscriptFieldsFragment[],
  emailThreads: EmailThreadConversationFieldsFragment[]
) => {
  if (calls?.at(-1)) {
    return (
      calls
        .at(-1)
        ?.transcript.filter(
          (t) => t.role === CallTranscriptRole.Agent || t.role === CallTranscriptRole.User
        )
        .filter((t) => !!t.content)
        .map((t) => ({
          role: t.role === CallTranscriptRole.Agent ? EmailSenderRole.Cara : EmailSenderRole.Client,
          content: t.content
        })) || []
    );
  }
  if (emailThreads?.[0]) {
    return [...emailThreads[0].participantEmails]
      .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime())
      .map((e) => ({ role: e.senderRole, content: e.body }));
  }
  return [];
};

export const ServiceRequestCommentsAndActivityPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
          agencyUsers: AgencyUserFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest, agencyUsers }) =>
          serviceRequest &&
          agencyUsers && (
            <CommentsAndActivityPage serviceRequest={serviceRequest} agencyUsers={agencyUsers} />
          )
        }
      </Await>
    </React.Suspense>
  );
};

const CommentsAndActivityPage = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow flex-grow flex-shrink-0">
      <div className="px-4 py-5 sm:px-6">
        <CommentsAndActivity
          serviceRequest={props.serviceRequest}
          agencyUsers={props.agencyUsers}
        />
      </div>
    </div>
  );
};

const relativeDate = (date: Date) => {
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const now = new Date();
  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  const units = [
    { unit: 'year', seconds: 31536000 },
    { unit: 'month', seconds: 2592000 },
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 }
  ];

  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds) {
      return rtf.format(Math.round(diffInSeconds / seconds), unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return rtf.format(0, 'second');
};

type ActivityItem = {
  id: string;
  type: string;
  person: string;
  date: string;
  comment: string | undefined;
  assigned: string | undefined;
  dueDate: string | undefined;
};

const CommentsAndActivity = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const submit = useSubmit();
  const navigation = useNavigation();
  const canComment = usePermissions().hasAllPermissions(UPDATE_SERVICE_REQUEST);

  const [, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<AddServiceRequestCommentInput>({
    content: ''
  });
  const { updateForm } = useFormActions(setForm, setValidationErrors);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form?.content) {
      return;
    }

    e.preventDefault();

    submit(form, { method: 'POST', action: 'comment', encType: 'application/json' });
    setForm({ content: '' });
  };

  const agencyUserMap = Object.fromEntries(props.agencyUsers.map((u) => [u.id, u]));
  const activity = [
    ...props.serviceRequest.comments,
    ...(props.serviceRequest.versionHistory?.filter((v) =>
      v.updateTypes.some((u) =>
        [
          ServiceRequestUpdateType.AssignServiceRequest,
          ServiceRequestUpdateType.CloseServiceRequest,
          ServiceRequestUpdateType.UpdateServiceRequestDueAt
        ].includes(u)
      )
    ) || [])
  ]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .flatMap((a) => {
      if (a.__typename === 'ServiceRequestComment') {
        return {
          id: a.id,
          type: 'comment',
          person: a.agencyUser.name,
          comment: a.content,
          date: relativeDate(new Date(a.createdAt)),
          assigned: undefined,
          dueDate: undefined
        } as ActivityItem;
      }
      if (a.__typename === 'ServiceRequestVersion') {
        return a.updateTypes.map((u) => {
          switch (u) {
            case ServiceRequestUpdateType.AssignServiceRequest:
              return {
                id: a.version.toString(),
                type: 'assignment',
                person: agencyUserMap[a.updateInitiatorId]?.name || 'unknown',
                assigned:
                  a.serviceRequest.assignee?.id === a.updateInitiatorId
                    ? 'themselves'
                    : a.serviceRequest.assignee?.name || 'no one',
                dueDate: undefined,
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;

            case ServiceRequestUpdateType.CloseServiceRequest:
              return {
                id: a.version.toString(),
                type: 'resolved',
                person:
                  a.updateInitiatorType === UpdateInitiatorType.AgencyUser
                    ? agencyUserMap[a.updateInitiatorId]?.name || 'unknown'
                    : 'Cara',
                comment: a.serviceRequest.resolution?.notes,
                assigned: undefined,
                dueDate: undefined,
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;

            case ServiceRequestUpdateType.UpdateServiceRequestDueAt:
              return {
                id: a.version.toString(),
                type: 'set due date',
                person: agencyUserMap[a.updateInitiatorId]?.name || 'unknown',
                assigned: undefined,
                dueDate: a.serviceRequest.dueAt
                  ? new Date(a.serviceRequest.dueAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })
                  : 'none',
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;
          }
        });
      }

      return null;
    })
    .filter((v): v is ActivityItem => !!v);

  return (
    <div>
      <ul role="list">
        {activity.map((activityItem, activityItemIdx) => (
          <li key={activityItem.id}>
            <div className="relative pb-8">
              {activityItemIdx !== activity.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-neutral-200 dark:bg-neutral-700"
                />
              ) : null}
              <div className="relative flex items-start space-x-3">
                {activityItem.type === 'comment' ? (
                  <>
                    <div className="relative">
                      <div className="flex size-10 items-center justify-center rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-500/20 ring-8 ring-white dark:ring-neutral-800">
                        {activityItem.person
                          .split(' ')
                          .map((name) => name[0])
                          .join('')
                          .toUpperCase()}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div>
                        <div className="text-sm">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {activityItem.person}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
                          Commented {activityItem.date}
                        </p>
                      </div>
                      <div className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
                        {activityItem.comment}
                      </div>
                    </div>
                  </>
                ) : activityItem.type === 'assignment' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 ring-8 ring-white dark:ring-neutral-800">
                          <UserCircleIcon
                            aria-hidden="true"
                            className="size-5 text-neutral-500 dark:text-neutral-400"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.person}
                        </span>{' '}
                        assigned{' '}
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.assigned}
                        </span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                    </div>
                  </>
                ) : activityItem.type === 'resolved' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-primary-500 ring-8 ring-white dark:ring-neutral-800">
                          <CheckIcon aria-hidden="true" className="size-5 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.person}
                        </span>{' '}
                        resolved{' '}
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.assigned}
                        </span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                      <div className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
                        {activityItem.comment}
                      </div>
                    </div>
                  </>
                ) : activityItem.type === 'set due date' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 ring-8 ring-white dark:ring-neutral-800">
                          <CalendarIcon
                            aria-hidden="true"
                            className="size-5 text-neutral-500 dark:text-neutral-400"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.person}
                        </span>{' '}
                        set the due date to{' '}
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {activityItem.dueDate}
                        </span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {!canComment ? (
        <PermissionNotice>
          You can read this service request but not comment on it or change it.
        </PermissionNotice>
      ) : (
        <div className="flex items-start space-x-4">
          <div className="shrink-0">
            <div className="flex size-10 p-2.5 items-center justify-center rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-500/20 ring-8 ring-white dark:ring-neutral-800">
              <UserIcon />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <Form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="comment" className="sr-only">
                  Add your comment
                </label>
                <Textarea
                  rows={3}
                  placeholder="Add your comment..."
                  value={form.content}
                  onChange={(e) => {
                    const content = e.currentTarget.value;
                    updateForm(['content'], (prev) => ({ ...prev, content }));
                  }}
                />
              </div>

              <Button
                type="submit"
                color="sky"
                loading={navigation.state === 'submitting'}
                disabled={form.content.trim().length === 0}
              >
                Comment
              </Button>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};
